<template>
  <div>
    <v-data-table
      :headers="selectedHeaders"
      :items="data"
      :rows-per-page-text="$t('support.components.dataTable.rowsPerPageText')"
      :no-data-text="$t('support.components.dataTable.noDataText')"
      :no-results-text="$t('support.components.dataTable.noResultsText')"
      hide-actions
      :loading="loading"
      disable-initial-sort
    >
      <template v-slot:headers="props">
        <draggable
          v-model="selectedHeaders"
          :force-fallback="true"
          :fallback-on-body="true"
          :empty-insert-threshold="true"
          tag="tr"
          @change="headersOrderUpdated($event)"
        >
          <th
            v-if="isSelected"
            align="center"
          >
            <v-checkbox
              v-model="selectedAll"
              hide-details
              @change="updateAllSelectedRows(selectedAll)"
            />
          </th>
          <th
            v-for="header in props.headers"
            :key="header.name"
            class="header-draggable"
            :align="header.align || 'left'"
          >
            <data-table-header-cell
              v-if="header.component !== 'DataTableCheckBoxColumn'"
              :header="header"
              @update-order="orderUpdated($event)"
            />
            <data-table-select-all-cell
              v-if="allowSelectAll && header.component === 'DataTableCheckBoxColumn'"
              :header="header"
              :status="getSelectedStatus"
              @updated-select-all-value="selectAllUpdated($event, header.value)"
            />
          </th>
          <th
            v-if="!(hideEdit && hideDelete) && data.length"
            align="center"
            width="200px"
          >
            Ações
          </th>
        </draggable>
      </template>
      <template v-slot:items="props">
        <tr :style="{backgroundColor: getPropByString(props.item, columnColor)}">
          <td
            v-if="isSelected"
            align="center"
            class="noWrap"
          >
            <v-checkbox
              :key="generateKey(props.index, props.item.id, 'select')"
              v-model="selectedAllRows[props.index].value"
              class="crud_list_select"
              hide-details
              @change="updateSelectedRows(props.index, selectedAllRows[props.index])"
            />
          </td>
          <td
            v-for="(header, key) in selectedHeaders"
            :key="generateKey(props.index, props.item.id, key)"
            :align="header.align || 'left'"
            :width="header.width"
            :class="header.class"
          >
            <component
              :is="header.component || 'DataTableTextColumn'"
              v-if="header.component !== 'DataTableCheckBoxColumn'"
              :key="generateKey(props.index, props.item.id)"
              :options="header.options"
              :height="header.height"
              :row="props.item"
              :style="{color: getPropByString(props.item, columnFontColor)}"
              :value="getPropByString(props.item, header.value, header.default)"
              :custom-action="customAction"
              @updated-value="updatedValue($event, props.item, header.value)"
            />

            <v-checkbox
              v-if="header.component === 'DataTableCheckBoxColumn'"
              :key="generateKey(props.index, props.item.id)"
              v-model="selectedRows"
              :value="props.item"
              hide-details
              @change="updatedSelectedRows()"
            />
          </td>
          <td
            v-if="!(hideEdit && hideDelete)"
            align="center"
            class="noWrap"
          >
            <v-btn
              v-if="!hideEdit"
              icon
              class="dataTableEditButton"
              @click="onClickEdit(props.item)"
            >
              <v-icon color="#645E5E">
                {{ editIcon }}
              </v-icon>
            </v-btn>
            <v-btn
              v-if="!hideDelete"
              icon
              class="dataTableDeleteButton"
              @click="onClickDelete(props.item)"
            >
              <v-icon color="#645E5E">
                delete
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>
    <div
      v-if="hideDataTableBottom"
      class="mx-4"
    >
      <v-layout
        align-center
        justify-space-between
      >
        <v-flex
          class="text-xs-left"
          shrink
        >
          <v-layout align-center>
            <v-flex shrink>
              {{ $t('support.components.dataTable.rowsPerPageText') }}
            </v-flex>
            <v-flex
              shrink
              class="px-1"
            >
              <v-select
                v-model="pagination.limit"
                class="mr-2 rowsPerPageSelect"
                :items="[15, 30, 60]"
                dense
              />
            </v-flex>
            <v-flex shrink>
              {{ totalItemsText }} de {{ totalItems }}
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex shrink>
          <v-pagination
            v-model="pagination.page"
            :length="pages"
            :total-visible="7"
            circle
          />
        </v-flex>
        <v-flex
          shrink
          class="text-xs-right"
        >
          <data-table-config
            v-model="selectedHeaders"
            :items="headers"
            :table-headers-key="tableName"
          />
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import DataTableConfig from 'Support/components/data-table/DataTableConfig.vue';
import DataTableHeaderCell from 'Support/components/data-table/DataTableHeaderCell.vue';
import DataTableSelectAllCell from 'Support/components/data-table/DataTableSelectAllCell.vue';
import DataTableTextColumn from 'Support/components/data-table/DataTableColumns/DataTableTextColumn.vue';
import DataTableDateColumn from 'Support/components/data-table/DataTableColumns/DataTableDateColumn.vue';
import DataTableWeightColumn from 'Support/components/data-table/DataTableColumns/DataTableWeightColumn.vue';
import DataTableCheckBoxColumn from 'Support/components/data-table/DataTableColumns/DataTableCheckBoxColumn.vue';
import DataTableStarColumn from 'Support/components/data-table/DataTableColumns/DataTableStarColumn.vue';
import DataTableJoinedColumn from 'Support/components/data-table/DataTableColumns/DataTableJoinedColumn.vue';
import DataTableArrayToStringColumn from 'Support/components/data-table/DataTableColumns/DataTableArrayToStringColumn.vue';
import DataTableCurrencyColumn from 'Support/components/data-table/DataTableColumns/DataTableCurrencyColumn.vue';
import DataTableCnpjColumn from 'Support/components/data-table/DataTableColumns/DataTableCnpjColumn.vue';
import DataTableSelectionStatus from 'Support/enums/DataTableSelectionStatus';
import DataTableChipStatusColumn from 'Support/components/data-table/DataTableColumns/DataTableChipStatusColumn.vue';
import DataTableCpfColumn from 'Support/components/data-table/DataTableColumns/DataTableCpfColumn.vue';
import DataTableCpfCnpjColumn from 'Support/components/data-table/DataTableColumns/DataTableCpfCnpjColumn.vue';
import DataTableService from 'Support/services/DatatableService';
import DataTableHighlightColumn from 'Support/components/data-table/DataTableColumns/DataTableHighlightColumn.vue';

export default {
  name: 'DataTable',
  components: {
    Draggable,
    DataTableTextColumn,
    DataTableDateColumn,
    DataTableConfig,
    DataTableHeaderCell,
    DataTableCheckBoxColumn,
    DataTableStarColumn,
    DataTableArrayToStringColumn,
    DataTableWeightColumn,
    DataTableCurrencyColumn,
    DataTableCpfCnpjColumn,
    DataTableSelectAllCell,
    DataTableJoinedColumn,
    DataTableCnpjColumn,
    DataTableChipStatusColumn,
    DataTableCpfColumn,
    DataTableHighlightColumn,
  },
  props: {
    hideEdit: {
      type: Boolean,
      default: false,
    },
    hideDelete: {
      type: Boolean,
      default: false,
    },
    hideDataTableBottom: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    columnColor: {
      type: String,
      default: '',
    },
    columnFontColor: {
      type: String,
      default: '',
    },
    allowSelectAll: {
      type: Boolean,
      default: false,
    },
    disableSetQueryString: {
      type: Boolean,
      default: false,
    },
    showColumnConfig: {
      type: Boolean,
      default: false,
    },
    tableName: {
      type: String,
      default: '',
    },
    customAction: {
      type: Function,
      default: null,
    },
    editIcon: {
      type: String,
      default() {
        return 'edit';
      },
    },
    selectAll: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data: vm => ({
    selectedAll: false,
    selectedHeaders: [],
    pagination: {
      page: parseInt(vm.$route.query.page, 10) || 1,
      limit: parseInt(vm.$route.query.limit, 10) || 15,
    },
    selectedRows: [],
  }),
  computed: {
    isSelected() {
      return this.allowSelectAll && this.data.length;
    },
    totalItemsText() {
      const { pagination } = this;
      const actualRows = pagination.limit * (pagination.page - 1) + 1;
      let show = pagination.limit * pagination.page;
      if (show > this.totalItems) {
        show = this.totalItems;
      }

      return `${actualRows} - ${show}`;
    },
    pages() {
      if (this.pagination.limit == null || this.totalItems == null) return 0;

      return Math.ceil(this.totalItems / this.pagination.limit);
    },
    getSelectedStatus() {
      const selectedCount = this.selectedRows.length;
      if (selectedCount === this.data.length) {
        return DataTableSelectionStatus.ALL_SELECTED;
      }

      if (selectedCount > 0 && selectedCount < this.data.length) {
        return DataTableSelectionStatus.ONE_OR_MORE_SELECTED;
      }

      return DataTableSelectionStatus.NONE_SELECTED;
    },
    getData() {
      return this.data;
    },
    selectedAllRows: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    getSelectedStatus: {
      handler(value) {
        this.$emit('select-all-status-change', value);
      },
    },
    'pagination.page': {
      handler(value) {
        this.$emit('update-page', value);
      },
    },
    'pagination.limit': {
      handler(value) {
        this.$emit('update-rows-per-page', value);
      },
    },
    '$route.query': {
      handler(value) {
        if (value.page && !this.disableSetQueryString) {
          this.pagination.page = parseInt(value.page, 10);
        }
        this.selectAllUpdated(false);
      },
    },
  },
  created() {
    const localHeaders = DataTableService.getHeaders(this.tableName);
    if (localHeaders) {
      this.selectedHeaders = localHeaders;
      const newHeaders = localHeaders.map((header) => {
        const headerCorrespondente = this.headers.find(item => item.text === header.text);
        if (!headerCorrespondente) {
          return null;
        }
        return {
          ...headerCorrespondente,
          ...header,
        };
      }).filter(item => item !== null);
      this.selectedHeaders = newHeaders;
    } else {
      this.selectedHeaders = this.headers;
    }
  },
  updated() {
    if (this.data.length && !this.selectedAllRows.length) {
      this.selectedAllRows = this.data.map(({ id }) => ({ id, value: false }));
    }
  },
  methods: {
    onClickEdit(item) {
      this.$emit('edit', item);
    },
    onClickDelete(item) {
      this.$emit('delete', item);
    },
    getPropByString(obj, desc, defaultValue = '') {
      return desc.split('.').reduce((a, b) => ((typeof a[b] === 'boolean') ? a[b] : (a[b] || defaultValue)), obj);
    },
    orderUpdated(event) {
      this.$emit('update-order', event);
    },
    updatedValue(item) {
      this.$emit('update-row', item);
    },
    updatedSelectedRows() {
      this.$emit('update-selected-rows', this.selectedRows);
    },
    selectAllUpdated(selected) {
      this.selectedRows = selected ? this.data : [];
      this.$emit('updated-select-all-value', this.selectedRows);
    },
    generateKey(index, value, prefix = '') {
      return `${prefix}${(index * this.pagination.page) + this.pagination.limit + value}`;
    },
    headersOrderUpdated() {
      DataTableService.saveHeaders(this.tableName, this.selectedHeaders);
    },
    updateSelectedRows(_, row) {
      this.updatedValue(row);
    },
    updateAllSelectedRows(value) {
      this.selectedAllRows = this.selectedAllRows.reduce(
        (arr, { id }) => {
          const row = {
            id,
            value,
          };

          if (row.value) {
            this.updateSelectedRows(null, row);
          }

          arr.push(row);

          return arr;
        },
        [],
      );
    },
    emiteUpdateRows() {
      this.$emit('update-rows', this.selectedAllRows);
    },
  },
};
</script>

<style lang="stylus">
.header-draggable
  cursor grab

.rowsPerPageSelect
  width 40px

.sortable-drag
  color rgba(0,0,0,0.54)
  font-weight normal
</style>
